// ====================================================
// VidyaTrack - Notices & Announcements Page
// ====================================================

import React, { useState, useEffect } from "react";
import { FiPlus, FiSearch, FiBell, FiStar } from "react-icons/fi";
import { useAuth } from "../contexts/AuthContext";
import { useData } from "../contexts/DataContext";
import Modal from "../components/shared/Modal";
import NoticeCard from "../components/shared/NoticeCard";
import { toast } from "react-toastify";

// Safe date converter (handles Firestore Timestamps)





export default function NoticesPage() {
  const { userData } = useAuth();
  const { getNotices, addNotice, deleteNotice } = useData();
  const role = userData?.role;
  const canPost = role === "admin" || role === "teacher";

  const [notices, setNotices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterAudience, setFilterAudience] = useState("all");
  const [filterPriority, setFilterPriority] = useState("all");
  const [showModal, setShowModal] = useState(false);
  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState({
    title: "", content: "", audience: "all", priority: "normal", pinned: false
  });
  const [errors, setErrors] = useState({});

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadNotices(); }, []);

  const loadNotices = async () => {
    setLoading(true);
    try {
      const audience = role === "student" ? "students" : role === "parent" ? "parents" : undefined;
      const data = await getNotices(audience ? { audience } : {});
      setNotices(data);
    } catch (e) { toast.error("Failed to load notices"); }
    finally { setLoading(false); }
  };

  const validate = () => {
    const errs = {};
    if (!form.title.trim()) errs.title = "Title is required";
    if (!form.content.trim()) errs.content = "Content is required";
    if (form.title.length > 100) errs.title = "Title too long";
    return errs;
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }
    setSaving(true);
    try {
      await addNotice({ ...form, author: userData?.name, authorRole: role });
      toast.success("Notice posted successfully!");
      setShowModal(false);
      setForm({ title: "", content: "", audience: "all", priority: "normal", pinned: false });
      setErrors({});
      loadNotices();
    } catch (e) { toast.error("Failed to post notice"); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this notice?")) return;
    try {
      await deleteNotice(id);
      toast.success("Notice deleted");
      loadNotices();
    } catch { toast.error("Failed to delete"); }
  };

  const filtered = notices.filter(n => {
    const matchSearch = n.title.toLowerCase().includes(search.toLowerCase()) ||
      n.content.toLowerCase().includes(search.toLowerCase());
    const matchAud = filterAudience === "all" || n.audience === filterAudience;
    const matchPrio = filterPriority === "all" || n.priority === filterPriority;
    return matchSearch && matchAud && matchPrio;
  });

  const pinned = filtered.filter(n => n.pinned);
  const regular = filtered.filter(n => !n.pinned);

  return (
    <div className="animate-fade">
      <div className="page-header">
        <div>
          <h1 className="page-title">📢 Notices & Announcements</h1>
          <p className="page-subtitle">{filtered.length} notices</p>
        </div>
        {canPost && (
          <button className="btn btn-primary" onClick={() => setShowModal(true)}>
            <FiPlus size={16} /> Post Notice
          </button>
        )}
      </div>

      {/* Filters */}
      <div style={{ display: "flex", gap: 12, marginBottom: 24, flexWrap: "wrap" }}>
        <div className="search-box" style={{ flex: 1, minWidth: "200px" }}>
          <FiSearch className="search-icon" />
          <input placeholder="Search notices..." value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        {canPost && (
          <select className="form-control" style={{ width: "auto" }} value={filterAudience} onChange={e => setFilterAudience(e.target.value)}>
            <option value="all">All Audience</option>
            <option value="students">Students</option>
            <option value="parents">Parents</option>
            <option value="teachers">Teachers</option>
          </select>
        )}
        <select className="form-control" style={{ width: "auto" }} value={filterPriority} onChange={e => setFilterPriority(e.target.value)}>
          <option value="all">All Priority</option>
          <option value="high">Important</option>
          <option value="normal">General</option>
        </select>
      </div>

      {loading ? (
        <div className="loading-center"><div className="spinner" /></div>
      ) : (
        <div>
          {/* Pinned Notices */}
          {pinned.length > 0 && (
            <div style={{ marginBottom: 24 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
                <FiStar size={16} style={{ color: "var(--amber-600)" }} />
                <h3 style={{ fontSize: "0.9rem", fontWeight: 600, color: "var(--gray-700)" }}>PINNED NOTICES</h3>
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                {pinned.map(n => (
                  <NoticeCard key={n.id} notice={n} onDelete={handleDelete} canDelete={canPost} />
                ))}
              </div>
            </div>
          )}

          {/* Regular Notices */}
          {regular.length > 0 && (
            <div>
              {pinned.length > 0 && (
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
                  <FiBell size={16} style={{ color: "var(--blue-700)" }} />
                  <h3 style={{ fontSize: "0.9rem", fontWeight: 600, color: "var(--gray-700)" }}>ALL NOTICES</h3>
                </div>
              )}
              <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                {regular.map(n => (
                  <NoticeCard key={n.id} notice={n} onDelete={handleDelete} canDelete={canPost} />
                ))}
              </div>
            </div>
          )}

          {filtered.length === 0 && (
            <div className="card">
              <div className="empty-state">
                <div className="empty-state-icon">🔔</div>
                <div className="empty-state-title">No notices found</div>
                <div className="empty-state-desc">
                  {canPost ? "Start by posting an announcement." : "No announcements at the moment."}
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Add Notice Modal */}
      <Modal
        show={showModal}
        onClose={() => { setShowModal(false); setErrors({}); }}
        title="Post New Notice"
        footer={<>
          <button className="btn btn-ghost" onClick={() => setShowModal(false)}>Cancel</button>
          <button className="btn btn-primary" onClick={handleAdd} disabled={saving}>
            {saving ? <><div className="spinner spinner-sm" /> Posting...</> : "Post Notice"}
          </button>
        </>}
      >
        <div className="form-group">
          <label className="form-label">Title *</label>
          <input
            className={`form-control${errors.title ? " error" : ""}`}
            placeholder="Notice title"
            value={form.title}
            onChange={e => { setForm(p => ({ ...p, title: e.target.value })); setErrors(p => ({ ...p, title: "" })); }}
          />
          {errors.title && <div className="form-error">{errors.title}</div>}
        </div>
        <div className="form-group">
          <label className="form-label">Content *</label>
          <textarea
            className={`form-control${errors.content ? " error" : ""}`}
            rows={5}
            placeholder="Write the full notice content here..."
            value={form.content}
            onChange={e => { setForm(p => ({ ...p, content: e.target.value })); setErrors(p => ({ ...p, content: "" })); }}
          />
          {errors.content && <div className="form-error">{errors.content}</div>}
          <div className="form-hint">{form.content.length} characters</div>
        </div>
        <div className="g g2">
          <div className="form-group">
            <label className="form-label">Audience</label>
            <select className="form-control" value={form.audience} onChange={e => setForm(p => ({ ...p, audience: e.target.value }))}>
              <option value="all">Everyone</option>
              <option value="students">Students Only</option>
              <option value="parents">Parents Only</option>
              <option value="teachers">Teachers Only</option>
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">Priority</label>
            <select className="form-control" value={form.priority} onChange={e => setForm(p => ({ ...p, priority: e.target.value }))}>
              <option value="normal">General</option>
              <option value="high">Important</option>
            </select>
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <input
            type="checkbox"
            id="pinned"
            checked={form.pinned}
            onChange={e => setForm(p => ({ ...p, pinned: e.target.checked }))}
            style={{ width: 16, height: 16, accentColor: "var(--blue-700)" }}
          />
          <label htmlFor="pinned" style={{ fontSize: "0.88rem", color: "var(--gray-700)", cursor: "pointer" }}>
            📌 Pin this notice (shows at top)
          </label>
        </div>
      </Modal>
    </div>
  );
}
